import { useEffect, useState, useCallback } from "react";
import { fetchServiceCatalog, createServiceCatalog, updateServiceCatalog, deleteServiceCatalog, fmtMoney } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { nome: "", descricao: "", preco_padrao: 0 };

export default function ServicesCatalog() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const reload = useCallback(() => fetchServiceCatalog().then(setList), []);
  useEffect(() => { reload(); }, [reload]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome) { toast.error("Nome é obrigatório"); return; }
    const payload = { ...form, preco_padrao: parseFloat(form.preco_padrao || 0) };
    try {
      if (editId) await updateServiceCatalog(editId, payload);
      else await createServiceCatalog(payload);
      toast.success("Serviço salvo");
      setOpen(false); setForm(empty); setEditId(null);
      reload();
    } catch { toast.error("Erro ao salvar"); }
  };

  const onEdit = (s) => { setEditId(s.id); setForm({ ...s }); setOpen(true); };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir este serviço?")) return;
    await deleteServiceCatalog(id);
    toast.success("Serviço excluído");
    reload();
  };

  return (
    <div data-testid="services-page">
      <PageHeader
        title="Catálogo de Serviços"
        subtitle="Serviços padrão da oficina com preços de referência."
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-service-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">
                <Plus size={16} weight="bold" className="mr-1.5" /> Novo Serviço
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[480px]">
              <DialogHeader>
                <DialogTitle className="font-heading">{editId ? "Editar Serviço" : "Novo Serviço"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label>Nome *</Label>
                  <Input data-testid="service-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
                </div>
                <div>
                  <Label>Descrição</Label>
                  <Textarea data-testid="service-desc" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
                </div>
                <div>
                  <Label>Preço padrão (R$)</Label>
                  <Input data-testid="service-preco" type="number" step="0.01" value={form.preco_padrao} onChange={(e) => setForm({ ...form, preco_padrao: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                  <Button type="submit" data-testid="save-service-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">Salvar</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-[#E2E8F0] rounded-md">
        {list.length === 0 ? (
          <EmptyState title="Nenhum serviço cadastrado" message="Cadastre serviços padrão para agilizar o preenchimento das ordens." />
        ) : (
          <Table>
            <TableHeader className="bg-[#F8FAFC]">
              <TableRow>
                <TableHead>Serviço</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead className="text-right">Preço Padrão</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((s, i) => (
                <TableRow key={s.id} className={i % 2 ? "bg-[#F8FAFC]" : ""}>
                  <TableCell className="font-medium">{s.nome}</TableCell>
                  <TableCell className="text-[#475569] text-sm">{s.descricao || "-"}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtMoney(s.preco_padrao)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => onEdit(s)}>
                      <PencilSimple size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onDelete(s.id)}>
                      <Trash size={16} className="text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
